/**
 * Selection Context Menu Plugin for Lexical
 * Shows a context menu when text is selected and user right-clicks (or long-presses on mobile).
 * Provides options for tagging, encryption, dependency declaration, and a "strong moment" toggle.
 *
 * Desktop: tall vertical menu with labels + shortcuts.
 * Touch: compact horizontal toolbar positioned above the selection, icon-only with title tooltips.
 *   Includes Cut / Copy / Paste / Bold up front so the native callout we replace still covers the
 *   common text actions.
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  ArrowDownward,
  ArrowUpward,
  Bolt,
  Chat as ChatIcon,
  ContentCopy,
  ContentCut,
  ContentPaste,
  FormatBold,
  Label,
  Link as LinkIcon,
  Security,
} from '@mui/icons-material'
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material'
import { MOMENT_TAG_ID } from '@shared/tags/canonicalMomentTag'
import { $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND } from 'lexical'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import { ContentAST } from '../../../../transforms/ast'
import { Annotation } from '../../../../types/annotations'
import { getAnnotationsForBlock } from '../../../../utils/annotationUtils'
import { COPY_EMBED_COMMAND, PASTE_EMBED_COMMAND } from '../embedClipboard'
import {
  ADD_DEP_AFTER_COMMAND,
  ADD_DEP_BEFORE_COMMAND,
  ASK_ASSISTANT_COMMAND,
  MARK_ENCRYPTION_COMMAND,
  TAG_SELECTION_COMMAND,
  TOGGLE_MOMENT_COMMAND,
} from '../LexicalToolbar'

export interface SelectionContextMenuPluginProps {
  /** Whether the current user is an author (only authors can encrypt) */
  isAuthor?: boolean
  /** Whether to enable the custom selection context menu on touch/mobile devices */
  enableOnMobile?: boolean
  /** Annotations for the chapter — used to label the "strong moment" toggle. */
  annotations?: Annotation[]
  /** AST for the chapter — used to walk blocks when checking moment coverage. */
  ast?: ContentAST
}

/** Walk a Lexical node up to its block ancestor and return that block's id. */
function findBlockIdFromLexicalNode(node: { __blockId?: string; getParent?: () => unknown } | null): string | null {
  if (!node) {
    return null
  }
  if (node.__blockId) {
    return node.__blockId
  }
  const parent = node.getParent?.() as typeof node | null
  if (parent) {
    return findBlockIdFromLexicalNode(parent)
  }
  return null
}

function detectTouchDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }
  const hasTouchPoints = navigator.maxTouchPoints > 0
  const coarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
  return hasTouchPoints || coarsePointer
}

/**
 * Read the DOM selection's bounding rect at this moment. Used on touch to position
 * the toolbar above the highlighted text rather than at the long-press point.
 */
function getDomSelectionRect(): DOMRect | null {
  const sel = typeof window !== 'undefined' ? window.getSelection() : null
  if (!sel || sel.rangeCount === 0) {
    return null
  }
  const rect = sel.getRangeAt(0).getBoundingClientRect()
  if (!rect || (rect.width === 0 && rect.height === 0)) {
    return null
  }
  return rect
}

/** Estimated height of the touch toolbar — used to clamp it within the viewport. */
const TOUCH_TOOLBAR_HEIGHT_PX = 52
const TOUCH_TOOLBAR_MARGIN_PX = 12

interface OpenMenuState {
  top: number
  left: number
  /** Whether the selection at open time overlaps with a moment-tagged annotation. */
  selectionHasMoment: boolean
  hasSelection: boolean
}

export function SelectionContextMenuPlugin({
  isAuthor = false,
  enableOnMobile = false,
  annotations = [],
  ast,
}: SelectionContextMenuPluginProps) {
  const [editor] = useLexicalComposerContext()
  const [menuState, setMenuState] = useState<OpenMenuState | null>(null)

  // Mobile/touch detection — frozen at mount so menu items render consistently.
  const isTouch = useMemo(() => detectTouchDevice(), [])

  // Handle context menu event
  useEffect(() => {
    if (!isAuthor) {
      return
    }

    const rootElement = editor.getRootElement()
    if (!rootElement) {
      return
    }

    const handleContextMenu = (event: MouseEvent) => {
      if (!enableOnMobile && isTouch) {
        return
      }

      editor.getEditorState().read(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) {
          return
        }
        const hasSelection = !selection.isCollapsed() && selection.getTextContent().length > 0

        // Compute moment-coverage state at open time so the label is stable while the menu is open.
        // Endpoint block ids may be either top-level blocks (paragraphs etc.) or list items —
        // both are tagged via BlockAnchor, so we check whichever applies.
        let selectionHasMoment = false
        if (ast && annotations.length > 0) {
          const startBlockId = findBlockIdFromLexicalNode(selection.anchor.getNode() as never)
          const endBlockId = findBlockIdFromLexicalNode(selection.focus.getNode() as never)
          if (startBlockId && endBlockId) {
            const idsToCheck = new Set<string>([startBlockId, endBlockId])
            const startIndex = ast.blocks.findIndex((b) => b.id === startBlockId)
            const endIndex = ast.blocks.findIndex((b) => b.id === endBlockId)
            if (startIndex !== -1 && endIndex !== -1) {
              const lo = Math.min(startIndex, endIndex)
              const hi = Math.max(startIndex, endIndex)
              for (let i = lo; i <= hi; i++) {
                idsToCheck.add(ast.blocks[i].id)
              }
            }
            for (const blockId of idsToCheck) {
              const covering = getAnnotationsForBlock(annotations, blockId, ast)
              if (covering.some((a) => a.tags.includes(MOMENT_TAG_ID))) {
                selectionHasMoment = true
                break
              }
            }
          }
        }

        event.preventDefault()

        // On touch, anchor above the selection so the toolbar doesn't cover the text
        // or the selection handles. Fall back to event coords if no rect is available.
        let top = event.clientY
        let left = event.clientX
        if (isTouch) {
          const rect = getDomSelectionRect()
          if (rect) {
            const desiredTop = rect.top - TOUCH_TOOLBAR_HEIGHT_PX - TOUCH_TOOLBAR_MARGIN_PX
            // If there's no room above (selection near top), place below the selection instead.
            top = desiredTop > 8 ? desiredTop : rect.bottom + TOUCH_TOOLBAR_MARGIN_PX
            left = rect.left + rect.width / 2
          }
        }

        setMenuState({ top, left, selectionHasMoment, hasSelection })
      })
    }

    rootElement.addEventListener('contextmenu', handleContextMenu)

    return () => {
      rootElement.removeEventListener('contextmenu', handleContextMenu)
    }
  }, [editor, isAuthor, isTouch, enableOnMobile, annotations, ast])

  const handleClose = useCallback(() => {
    // Blur the focused menu item before unmounting so MUI's aria-hidden on the closing
    // wrapper doesn't sit on a focused descendant (Chrome warns about that combination).
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    setMenuState(null)
  }, [])

  // While the menu is open, dismiss on scroll — the toolbar is positioned relative
  // to the selection on screen, so scrolling would leave it misaligned. Matches the
  // native mobile callout's behavior. Capture phase so we catch ancestor scrolls too.
  useEffect(() => {
    if (menuState === null) {
      return
    }
    const onScroll = () => handleClose()
    window.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true })
    }
  }, [menuState, handleClose])

  // On touch the toolbar lets pointer events pass through to the editor behind it
  // (so the selection handles stay draggable), which means there's no backdrop to
  // click away on. Dismiss when the selection collapses — i.e. the user tapped
  // elsewhere or finished editing. Extending the selection keeps it non-collapsed,
  // so the toolbar stays put while the range grows.
  useEffect(() => {
    if (!isTouch || menuState === null) {
      return
    }
    const onSelectionChange = () => {
      const sel = typeof window !== 'undefined' ? window.getSelection() : null
      if (!sel || sel.isCollapsed || sel.toString().length === 0) {
        handleClose()
      }
    }
    document.addEventListener('selectionchange', onSelectionChange)
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange)
    }
  }, [isTouch, menuState, handleClose])

  const handleCopyEmbed = useCallback(() => {
    editor.dispatchCommand(COPY_EMBED_COMMAND, undefined)
    handleClose()
  }, [editor, handleClose])

  const handlePasteEmbed = useCallback(() => {
    editor.dispatchCommand(PASTE_EMBED_COMMAND, undefined)
    handleClose()
  }, [editor, handleClose])

  const handleEncrypt = useCallback(() => {
    editor.dispatchCommand(MARK_ENCRYPTION_COMMAND, undefined)
    handleClose()
  }, [editor, handleClose])

  const handleAskAssistant = useCallback(() => {
    editor.dispatchCommand(ASK_ASSISTANT_COMMAND, undefined)
    handleClose()
  }, [editor, handleClose])

  const handleTagSelection = useCallback(() => {
    editor.dispatchCommand(TAG_SELECTION_COMMAND, undefined)
    handleClose()
  }, [editor, handleClose])

  const handleToggleMoment = useCallback(() => {
    editor.dispatchCommand(TOGGLE_MOMENT_COMMAND, undefined)
    handleClose()
  }, [editor, handleClose])

  const handleAddDepBefore = useCallback(() => {
    editor.dispatchCommand(ADD_DEP_BEFORE_COMMAND, undefined)
    handleClose()
  }, [editor, handleClose])

  const handleAddDepAfter = useCallback(() => {
    editor.dispatchCommand(ADD_DEP_AFTER_COMMAND, undefined)
    handleClose()
  }, [editor, handleClose])

  const handleBold = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
    handleClose()
  }, [editor, handleClose])

  // Cut / Copy use document.execCommand — selection is preserved because we preventDefault on mouseDown.
  const handleCut = useCallback(() => {
    document.execCommand('cut')
    handleClose()
  }, [handleClose])

  const handleCopy = useCallback(() => {
    document.execCommand('copy')
    handleClose()
  }, [handleClose])

  // Paste reads via navigator.clipboard (the menu click counts as user activation) and inserts at selection.
  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText()
      if (text) {
        editor.update(() => {
          const sel = $getSelection()
          if ($isRangeSelection(sel)) {
            sel.insertText(text)
          }
        })
      }
    } catch {
      toast.error('Clipboard access denied')
    } finally {
      handleClose()
    }
  }, [editor, handleClose])

  // Detect if user is on Mac for shortcut display (desktop only)
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)
  const modKey = isMac ? '⌘' : 'Ctrl'
  const altKey = isMac ? '⌥' : 'Alt'

  if (!isAuthor) {
    return null
  }

  const momentLabel = menuState?.selectionHasMoment ? 'Unmark strong moment' : 'Mark as strong moment'

  // Touch: compact horizontal toolbar centered above the selection.
  if (isTouch) {
    return (
      <Menu
        open={menuState !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={menuState ? { top: menuState.top, left: menuState.left } : undefined}
        disableRestoreFocus
        disableAutoFocusItem
        disableScrollLock
        // Don't grab focus from the contenteditable — keeping focus on the editor preserves
        // the live selection so the user can keep dragging the handles to extend it.
        disableAutoFocus
        disableEnforceFocus
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        // Let touches fall through the modal root/backdrop to the editor behind (so the
        // selection handles stay draggable and the page can scroll); re-enable pointer
        // events on the toolbar itself so its buttons still respond. pointer-events is an
        // inherited CSS property, so root:none cascades to the invisible backdrop.
        slotProps={{
          root: { sx: { pointerEvents: 'none' } },
          paper: { sx: { pointerEvents: 'auto' } },
        }}
        MenuListProps={{
          sx: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            maxWidth: 'calc(100vw - 24px)',
            gap: 0.25,
            py: 0.5,
            px: 0.5,
          },
        }}
      >
        <IconButton
          size="small"
          disabled={!menuState?.hasSelection}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleCut}
          title="Cut"
        >
          <ContentCut fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          disabled={!menuState?.hasSelection}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleCopy}
          title="Copy"
        >
          <ContentCopy fontSize="small" />
        </IconButton>
        <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={handlePaste} title="Paste">
          <ContentPaste fontSize="small" />
        </IconButton>
        <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={handleBold} title="Bold">
          <FormatBold fontSize="small" />
        </IconButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />
        <IconButton
          size="small"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleTagSelection}
          title="Tag selection"
        >
          <Label fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleToggleMoment}
          title={momentLabel}
          sx={menuState?.selectionHasMoment ? { color: 'primary.main' } : undefined}
        >
          <Bolt fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleAskAssistant}
          title="Ask the assistant"
        >
          <ChatIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={handleCopyEmbed} title="העתק לשיבוץ">
          <LinkIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={handlePasteEmbed} title="הדבק כשיבוץ">
          <ContentPaste fontSize="small" />
        </IconButton>
      </Menu>
    )
  }

  // Desktop: full vertical menu with labels + shortcuts + dependency actions.
  return (
    <Menu
      open={menuState !== null}
      onClose={handleClose}
      anchorReference="anchorPosition"
      anchorPosition={menuState ? { top: menuState.top, left: menuState.left } : undefined}
      disableRestoreFocus
      disableAutoFocusItem
      disableAutoFocus
      disableEnforceFocus
    >
      {menuState?.hasSelection && (
        <MenuItem onMouseDown={(event) => event.preventDefault()} onClick={handleAskAssistant}>
          <ListItemIcon>
            <ChatIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Ask the Assistant</ListItemText>
        </MenuItem>
      )}
      {menuState?.hasSelection && <Divider />}
      {menuState?.hasSelection && (
        <MenuItem onMouseDown={(event) => event.preventDefault()} onClick={handleTagSelection}>
          <ListItemIcon>
            <Label fontSize="small" />
          </ListItemIcon>
          <ListItemText>Tag Selection</ListItemText>
          <Typography variant="body2" sx={{ ml: 2, color: 'var(--text-secondary)' }}>
            {modKey}+{altKey}+T
          </Typography>
        </MenuItem>
      )}
      {menuState?.hasSelection && (
        <MenuItem onMouseDown={(event) => event.preventDefault()} onClick={handleToggleMoment}>
          <ListItemIcon>
            <Bolt fontSize="small" />
          </ListItemIcon>
          <ListItemText>{momentLabel}</ListItemText>
        </MenuItem>
      )}
      {menuState?.hasSelection && (
        <MenuItem onMouseDown={(event) => event.preventDefault()} onClick={handleEncrypt}>
          <ListItemIcon>
            <Security fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark for Encryption</ListItemText>
          <Typography variant="body2" sx={{ ml: 2, color: 'var(--text-secondary)' }}>
            {modKey}+{altKey}+E
          </Typography>
        </MenuItem>
      )}
      {menuState?.hasSelection && <Divider />}
      {menuState?.hasSelection && (
        <MenuItem onMouseDown={(event) => event.preventDefault()} onClick={handleAddDepBefore}>
          <ListItemIcon>
            <ArrowUpward fontSize="small" />
          </ListItemIcon>
          <ListItemText>צריך לבוא לפני…</ListItemText>
        </MenuItem>
      )}
      {menuState?.hasSelection && (
        <MenuItem onMouseDown={(event) => event.preventDefault()} onClick={handleAddDepAfter}>
          <ListItemIcon>
            <ArrowDownward fontSize="small" />
          </ListItemIcon>
          <ListItemText>צריך לבוא אחרי…</ListItemText>
        </MenuItem>
      )}
      {menuState?.hasSelection && <Divider />}
      <MenuItem onMouseDown={(event) => event.preventDefault()} onClick={handleCopyEmbed}>
        <ListItemIcon>
          <LinkIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>העתק לשיבוץ</ListItemText>
      </MenuItem>
      <MenuItem onMouseDown={(event) => event.preventDefault()} onClick={handlePasteEmbed}>
        <ListItemIcon>
          <ContentPaste fontSize="small" />
        </ListItemIcon>
        <ListItemText>הדבק כשיבוץ</ListItemText>
      </MenuItem>
    </Menu>
  )
}

export default SelectionContextMenuPlugin
